import {Dependency} from '@mintdata-wsp/common/controllers/network/generated/responses/Dependency';
import {PackageData} from '@mintdata-wsp/common/controllers/network/generated/responses/PackageData';
import {DesignState, IDesignStateSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {CloudFunction} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {IOverlaySnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {FunctionSpec, IFunctionArgSpec} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';

export class IntegrationController
{
    private _packages:Array<PackageData>;
    private _designState:DesignState;
    private _organizationSid:string;
    private _publishedApplicationId:number;
    private _publishedApplicationAccessType:string;
    private _clientArtifactsHostname:string;
    private _cloudFunctions:Array<CloudFunction>;
    private _apiHostname:string;
    private _userlandPath:string;
    private _isStandaloneMode:boolean;
    private _areStandaloneBundles:boolean;

    public init()
    {
        const {_mintdataPublicConfig, _mintdataDesign} = global as any;
        const {
            organizationSid,
            publishedApplicationId,
            publishedApplicationAccessType,
            clientArtifactsHostname,
            apiHostname,
            userlandPath,
            cloudFunctionInfos,
            isStandaloneMode,
            areStandaloneBundles
        } = _mintdataPublicConfig;

        const {packages} = _mintdataDesign;

        this._organizationSid = organizationSid;
        this._publishedApplicationId = publishedApplicationId;
        this._publishedApplicationAccessType = publishedApplicationAccessType;
        this._clientArtifactsHostname = clientArtifactsHostname;
        this._apiHostname = apiHostname;
        this._userlandPath = userlandPath;
        this._isStandaloneMode = isStandaloneMode;
        this._areStandaloneBundles = areStandaloneBundles;
        this._cloudFunctions = this._populateFunctions(cloudFunctionInfos);
        this._packages = this._populatePackages(packages);
    }

    private _populateFunctions(cloudFunctionInfos:Array<any>):Array<CloudFunction>
    {
        return cloudFunctionInfos.map(info =>
        {
            const args = info.args.map(argInfo =>
            {
                return {
                    title: argInfo.title,
                    type: Type.fromTypeName(argInfo.typeName),
                };
            });
            args.unshift({
                title: 'api',
                type: SheetType.SheetAPI,
                tip: 'api'
            });

            const returns = {
                type: Type.Frame,
                title: 'Frame',
                tip: 'Returns a frame'
            } as IFunctionArgSpec;

            const spec = new FunctionSpec();
            spec.setValuesFrom({
                alias: info.name,
                returns
            });
            spec.args = args;

            return new CloudFunction(info.id, info.outputComponentId, spec);
        });
    }

    private _populatePackages(packages:Array<any>):Array<PackageData>
    {
        return packages.map(pkg => new PackageData(pkg));
    }

    public packageBundleFullUrl(pkg:PackageData)
    {
        //todo: this is kinda dumb - needs improvement
        if( this.isSSR )
        {
            //ssr variant:
            return `file://${this.userlandPath}/@${pkg.organizationId}/${pkg.name}/${pkg.version}/bundle.js`;
        }
        else
        {
            //browser variant:
            return `//${this._clientArtifactsHostname}/${pkg.filePath}`;
        }
    }

    public packageDepBundleFullUrl(dep:Dependency)
    {
        //todo: this is kinda dumb - needs improvement
        if( this.isSSR )
        {
            return dep.npmModule
                ? `file://${this.userlandPath}/@node_modules/${dep.name}/${dep.version}/bundle.js`
                : `file://${this.userlandPath}/@${dep.organization}/${dep.name}/${dep.version}/bundle.js`;
        }
        else
        {
            //browser variant:
            return `//${this._clientArtifactsHostname}/${dep.filePath}`;
        }
    }

    public get designStateSnapshot():IDesignStateSnapshot
    {
        const {_mintdataDesign: {design}} = global as any;
        return this._prepareDesignSnapshot(
            design.visualState ? design.visualState : design
        );
    }

    private _prepareDesignSnapshot(designSnapshot:IDesignStateSnapshot):IDesignStateSnapshot
    {
        if( !designSnapshot )
            return designSnapshot;

        if( designSnapshot.surface && designSnapshot.surface.pages )
        {
            Object.values(designSnapshot.surface.pages).forEach((page:any) =>
            {
                let overlaysForPublic = [];

                page.overlays.forEach((overlaySnapshot:IOverlaySnapshot) =>
                {
                    if( overlaySnapshot.isPinned )
                    {
                        overlaysForPublic.push(overlaySnapshot);
                    }
                });

                page.overlays = overlaysForPublic;
            });
        }

        return designSnapshot;
    }

    public get isSSR():boolean
    {
        return (global as any).isSSR;
    }

    public get userlandPath():string
    {
        Assert.yes(this._userlandPath, 'userlandPath is undefined');
        Assert.yes(this.isSSR, 'only for use in SSR mode');
        return this._userlandPath;
    }

    public get cloudFunctions():Array<CloudFunction>
    {
        return this._cloudFunctions;
    }

    public get packages():Array<PackageData>
    {
        return this._packages;
    }

    public get designState():DesignState
    {
        return this._designState;
    }

    public get organizationSid():string
    {
        return this._organizationSid;
    }

    public get publishedApplicationId():number
    {
        return this._publishedApplicationId;
    }

    public get publishedApplicationAccessType():string
    {
        return this._publishedApplicationAccessType;
    }

    public get clientArtifactsHostname():string
    {
        return this._clientArtifactsHostname;
    }

    public get apiHostname():string
    {
        return this._apiHostname;
    }

    public get isStandaloneMode():boolean
    {
        return this._isStandaloneMode;
    }

    public get areStandaloneBundles():boolean
    {
        return this._areStandaloneBundles;
    }
}
